/**
 * @fileoverview Componente individual de producto para el grid de selección
 * @module ProductGridItem
 * @category Component - Atom
 * @since 1.0.0
 * @version 1.0.0
 */
import React, { memo } from 'react';
import { truncate } from 'lodash';
import { formatMoney } from '../../../../util/numberUtils';
import { hasDiscount } from '../../../../util/productUtils';
import ProductOutOfStock from './ProductOutOfStock';

/**
 * @typedef {Object} Product
 * @description Objeto producto con información básica para display
 * @property {string} web_id - ID web del producto
 * @property {string} title - Título del producto
 * @property {number} quantity - Stock disponible
 * @property {Array} image_urls - URLs de imágenes del producto
 */

/**
 * Componente Atom para mostrar un producto individual en el grid.
 *
 * Implementa el patrón **Atom** de Atomic Design como unidad básica
 * reutilizable para mostrar información de productos.
 *
 * @component ProductGridItem
 * @category Component - Atom
 *
 * @description
 * **Características:**
 * - Diseño compacto optimizado para grids
 * - Manejo de imágenes con fallback
 * - Indicador de stock bajo
 * - Indicador de variantes disponibles
 * - Diseño responsivo con Tailwind CSS
 * - Optimizado con React.memo
 *
 * **Estados Visuales:**
 * - Normal: Producto con stock e imagen
 * - Sin imagen: Placeholder con ícono
 * - Stock bajo: Indicador visual de advertencia
 * - Con variantes: Etiqueta "Desde" en precio
 *
 * @param {Object} props - Propiedades del componente
 * @param {Product} props.product - Datos del producto
 * @param {string} props.currency - Código de moneda
 * @param {number} props.price - Precio a mostrar
 * @param {function} props.onSelect - Callback al seleccionar producto
 * @param {boolean} props.hasVariants - Si el producto tiene variantes
 *
 * @returns {JSX.Element} Elemento JSX renderizado con información del producto
 *
 * @example
 * ```jsx
 * <ProductGridItem
 *   product={productData}
 *   currency="USD"
 *   price={29.99}
 *   onSelect={() => handleSelect(productData)}
 *   hasVariants={true}
 * />
 * ```
 */
const ProductGridItem = memo(({
  product,
  currency,
  price,
  onSelect,
  hasVariants
}) => (
  <div className="tw-cursor-pointer tw-flex-1 tw-min-w-0" onClick={onSelect}>
    <div className="tw-overflow-hidden tw-flex tw-flex-col tw-gap-1.5 tw-text-left tw-justify-start">
      <div className="tw-relative">
        {product.image_urls && product.image_urls[0] ? (
          <img
            src={product.image_urls[0].url}
            referrerPolicy="no-referrer"
            alt={product.title}
            className="tw-rounded-lg tw-object-cover tw-w-full tw-aspect-[4/3]"
          />
        ) : (
          <div className="tw-rounded-lg tw-object-cover tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-w-full tw-aspect-[4/3]">
            <i className="mi-image-outline tw-text-xl tw-text-blue-500"></i>
          </div>
        )}
        <ProductOutOfStock product={product} />
      </div>

      <div className="tw-flex tw-flex-col tw-justify-between">
        <span className="tw-font-semibold tw-text-sm tw-text-gray-950">
          {truncate(product.title, { length: 18 })}
        </span>
        <span className="tw-font-normal tw-text-sm tw-text-gray-950">
          {hasVariants && 'Desde '}
          {currency}
          {formatMoney(hasDiscount(product) ? product.discounted_price : price)}
          {hasDiscount(product) && (
            <span className="tw-line-through tw-text-xs tw-text-gray-400 tw-ml-1.5">
              {currency}
              {formatMoney(price)}
            </span>
          )}
        </span>
        <span className="tw-font-normal tw-text-sm tw-text-gray-700">
          {product.quantity}
          {' '}
          stock
        </span>
      </div>
    </div>
  </div>
));

/**
 * Nombre de display para debugging y herramientas de desarrollo.
 *
 * @type {string}
 */
ProductGridItem.displayName = 'ProductGridItem';

export default ProductGridItem;
